import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { methodLevels, methodologyNames } from "config";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { usePatchCvMutation } from "../../../lib/cvsSlice";

// ── Main component ────────────────────────────────────────────────────────────

const Methods = ({ method, cvId }) => {
  const [localMethods, setLocalMethods] = useState(
    Array.isArray(method) ? method : []
  );
  const [searchValue, setSearchValue] = useState(null);
  const isDirty = useRef(false);

  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  // Guard against CvDetails polling resetting state while user edits
  useEffect(() => {
    if (isDirty.current) return;
    setLocalMethods(Array.isArray(method) ? method : []);
  }, [method, cvId]);

  // ── Search select → immediately add with default level ────────────────────
  const handleMethodSelect = async (_, selected) => {
    if (!selected) return;

    const selectedName =
      typeof selected === "string" ? selected.trim() : selected;
    if (!selectedName) return;

    const alreadyAdded = localMethods.some(
      (m) => m.group.toLowerCase() === selectedName.toLowerCase()
    );
    if (alreadyAdded) {
      toast.info(`${selectedName} is already added`);
      setSearchValue(null);
      return;
    }

    isDirty.current = true;
    setSearchValue(null);

    const optimisticId = `temp_${Date.now()}`;
    const optimisticMethod = { _id: optimisticId, group: selectedName };
    setLocalMethods((prev) => [...prev, optimisticMethod]);

    try {
      const result = await patchCv({
        cvId,
        methods: [{ group: selectedName }],
      }).unwrap();
      setLocalMethods(result?.methods ?? localMethods);
    } catch (err) {
      setLocalMethods((prev) => prev.filter((m) => m._id !== optimisticId));
      toast.error(`Failed to add: ${err?.data?.message ?? err.message}`);
    }
  };

  // ── Inline level update ───────────────────────────────────────────────────
  const handleLevelChange = async (m, newLevel) => {
    isDirty.current = true;
    setLocalMethods((prev) =>
      prev.map((item) => (item._id === m._id ? { ...item, level: newLevel } : item))
    );
    try {
      await patchCv({ cvId, methods: [{ id: m._id, level: newLevel }] }).unwrap();
    } catch (err) {
      setLocalMethods((prev) =>
        prev.map((item) => (item._id === m._id ? { ...item, level: m.level } : item))
      );
      toast.error("Failed to update level");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (methodId) => {
    isDirty.current = true;
    setLocalMethods((prev) => prev.filter((m) => m._id !== methodId));
    try {
      await patchCv({ cvId, methods: [{ id: methodId, delete: true }] }).unwrap();
    } catch (err) {
      setLocalMethods(localMethods);
      toast.error(`Failed to remove: ${err?.data?.message ?? err.message}`);
    }
  };

  const hasMethods = localMethods.length > 0;

  // NEXT stays disabled while any added method has no level chosen.
  const hasUnsetLevel = localMethods.some((m) => !m.level);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Search bar — always visible */}
      <Autocomplete
        value={searchValue}
        options={methodologyNames}
        freeSolo
        onChange={handleMethodSelect}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for method"
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        )}
      />

      {/* Methods list container */}
      {hasMethods && (
        <Box
          sx={{
            border: "1px solid #E0DEDD",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {/* Container header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              backgroundColor: "#F5F3F2",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#3D3634",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {localMethods.length} {localMethods.length === 1 ? "Method" : "Methods"} added
            </Typography>
          </Box>

          {/* Method rows */}
          {localMethods.map((m, index) => (
            <Box
              key={m._id}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1.25,
                backgroundColor: "#fff",
                borderTop: index === 0 ? "1px solid #E0DEDD" : "none",
                borderBottom: index < localMethods.length - 1 ? "1px solid #F5F3F2" : "none",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1A1A1A",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                {m.group}
              </Typography>

              <Select
                value={m.level ?? ""}
                displayEmpty
                size="small"
                onChange={(e) => handleLevelChange(m, e.target.value)}
                sx={{
                  minWidth: 130,
                  fontSize: "13px",
                  borderRadius: "6px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0DEDD" },
                }}
              >
                <MenuItem value="" disabled sx={{ fontSize: "13px" }}>
                  Select level
                </MenuItem>
                {methodLevels.map((lvl) => (
                  <MenuItem key={lvl} value={lvl} sx={{ fontSize: "13px" }}>
                    {lvl}
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size="small"
                onClick={() => handleDelete(m._id)}
                sx={{ color: "#C5BFBE", "&:hover": { color: "#E84610" } }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Navigation */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>
        <Button
          variant="contained"
          onClick={goToNextStep}
          disabled={hasUnsetLevel}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            "&:disabled": { backgroundColor: "#E8A090" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          NEXT
        </Button>
      </Box>
    </Box>
  );
};

export default Methods;
